import { Control, Icon, Image, Typography } from '@modulor/react';
import clx from 'classnames';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';

interface PendingWithdrawalCardProps {
  orderId: number;
  logo?: string;
  name: string;
  amount: string;
  returnAmount?: string;
  remainAmount?: string;
  checked: boolean;
  disabled?: boolean;
  errorText?: string;
  onToggle: (orderId: number) => void;
}

const EMPTY_VALUE = '—';

export const PendingWithdrawalCard: FC<PendingWithdrawalCardProps> = ({
  orderId,
  logo,
  name,
  amount,
  returnAmount,
  remainAmount,
  checked,
  disabled,
  errorText,
  onToggle,
}) => {
  const { translate } = useSkeletonTranslation();

  const rows = [
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.WITHDRAWAL_AMOUNT', { defaultValue: 'Withdrawal amount' }),
      value: amount,
    },
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.RETURN', { defaultValue: 'Return' }),
      value: returnAmount ?? EMPTY_VALUE,
      withInfo: true,
    },
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.REMAIN', { defaultValue: 'Remain' }),
      value: remainAmount ?? EMPTY_VALUE,
      withInfo: true,
    },
  ];

  return (
    <div
      className={clx('pending-withdrawal-card', { 'pending-withdrawal-card--disabled': disabled })}
      data-testid="pending-withdrawal-card"
    >
      <div className="pending-withdrawal-card__main">
        <div className="pending-withdrawal-card__logo">
          <Image src={logo} alt={name} width="24" height="24" />
        </div>
        <div className="pending-withdrawal-card__rows">
          {rows.map(({ label, value, withInfo }) => (
            <div key={label} className="pending-withdrawal-card__row">
              <span className="pending-withdrawal-card__label">
                <Typography variant="subhead-regular" tag="span" className="pending-withdrawal-card__label-text">
                  {label}
                </Typography>
                {/* TODO: wrap into Tooltip when texts are ready */}
                {withInfo && <Icon name="info_circle_outlined" size={12} />}
              </span>
              <Typography variant="subhead-regular" tag="span" className="pending-withdrawal-card__value">
                {value}
              </Typography>
            </div>
          ))}
        </div>
        <div className="pending-withdrawal-card__checkbox">
          <Control
            type="checkbox"
            name={`pending-withdrawal-${orderId}`}
            checked={checked}
            disabled={disabled}
            onChange={() => onToggle(orderId)}
          />
        </div>
      </div>
      {errorText && (
        <Typography variant="subhead-regular" tag="p" className="pending-withdrawal-card__error">
          {errorText}
        </Typography>
      )}
    </div>
  );
};
